import React, { useState, useEffect } from 'react';
import { 
  FiEye,        // View icon
  FiMessageCircle, // Message/Contact icon  
  FiRefreshCw   // Refresh icon
} from 'react-icons/fi';
import { IoSearchOutline } from 'react-icons/io5';
import { FaWhatsapp } from 'react-icons/fa';

import axios from 'axios';
import { CreateOrderButton } from './CreateOrder'; 
const OrdersDashboard = () => {
  const [activeTab, setActiveTab] = useState('paid');
  const [paidOrders, setPaidOrders] = useState([]);
  const [cartOrders, setCartOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [studentDetails, setStudentDetails] = useState({});
  const [courseDetails, setCourseDetails] = useState({});
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, [refreshTrigger]);
  const handleOrderCreated = () => {
    // Increment refreshTrigger to trigger useEffect
    setRefreshTrigger(prev => prev + 1);
  };

  // Fetch all required data
  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch summary statistics
      const statsResponse = await axios.get('https://apissc.sainikschoolcadet.com/api/StudentOrders/stats/summary');
      setStats(statsResponse.data);
      
      // Fetch completed orders
      const completedResponse = await axios.get('https://apissc.sainikschoolcadet.com/api/StudentOrders/all/completed');
      setPaidOrders(completedResponse.data);
      
      // Fetch cart orders
      const cartResponse = await axios.get('https://apissc.sainikschoolcadet.com/api/StudentOrders/all/cart');
      setCartOrders(cartResponse.data);
      
      // Get unique student IDs from both completed and cart orders
      const completedStudentIds = completedResponse.data.map(order => order.student_id);
      const cartStudentIds = cartResponse.data.map(order => order.student_id);
      const allStudentIds = [...new Set([...completedStudentIds, ...cartStudentIds])];
      
      // Get unique content IDs from all orders
      const completedContentIds = completedResponse.data.map(order => order.content_id);
      const cartContentIds = cartResponse.data.map(order => order.content_id);
      const allContentIds = [...new Set([...completedContentIds, ...cartContentIds])];
      
      // Fetch student details for each student ID
      const studentDetailsPromises = allStudentIds.map(async (studentId) => {
        try {
          const studentResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/students/getbyid/${studentId}`);
          return {
            [studentId]: {
              name: studentResponse.data.f_name,
              phone_number: studentResponse.data.phonenumber,
              email: studentResponse.data.email,
              class_entry: studentResponse.data.c_entry,
              school: studentResponse.data.c_school,
              gender: studentResponse.data.gender,
              state: studentResponse.data.state
            }
          };
        } catch (err) {
          console.error(`Failed to fetch student details for ID ${studentId}:`, err);
          return { [studentId]: { name: 'Unknown Student', phone_number: 'N/A' } };
        }
      });

      // Fetch course details for each content ID
      const courseDetailsPromises = allContentIds.map(async (contentId) => {
        try {
          const courseResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/Contents/${contentId}`);
          return {
            [contentId]: {
              title: courseResponse.data.title,
              description: courseResponse.data.description,
              content_type: courseResponse.data.content_type,
              thumbnail: courseResponse.data.thumbnail,
              sell_price: courseResponse.data.sell_price,
              max_price: courseResponse.data.max_price,
              access_end_date: courseResponse.data.access_end_date
            }
          };
        } catch (err) {
          console.error(`Failed to fetch course details for ID ${contentId}:`, err);
          return { [contentId]: { title: 'Unknown Course', sell_price: '0.00' } };
        }
      });

      // Resolve all promises and combine results
      const studentDetailsArray = await Promise.all(studentDetailsPromises);
      const studentDetailsMap = studentDetailsArray.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setStudentDetails(studentDetailsMap);
      
      const courseDetailsArray = await Promise.all(courseDetailsPromises);
      const courseDetailsMap = courseDetailsArray.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setCourseDetails(courseDetailsMap);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch order data. Please try again later.');
      setLoading(false);
    }
  };

  // Calculate total amount for completed orders by payment method
  const getTotalByPaymentMethod = (paymentMethod) => {
    if (!stats) return 0;
    const statItem = stats.find(item => 
      item.status === "completed" && item.payment_method === paymentMethod
    );
    return statItem ? parseFloat(statItem.total_amount) : 0;
  };

  // Calculate total amount for all completed orders
  const getTotalCompleted = () => {
    if (!stats) return 0;
    return stats
      .filter(item => item.status === "completed")
      .reduce((sum, item) => sum + parseFloat(item.total_amount), 0);
  };

  // Calculate total count for all completed orders
  const getTotalCompletedCount = () => {
    if (!stats) return 0;
    return stats
      .filter(item => item.status === "completed")
      .reduce((sum, item) => sum + parseInt(item.count), 0);
  };

  // Calculate total amount for cart - use sell_price from course details
  const getCartTotal = () => {
    if (!cartOrders || cartOrders.length === 0 || !courseDetails) return 0;
    
    // Sum up the sell_price of each course in the cart
    return cartOrders.reduce((total, order) => {
      const course = courseDetails[order.content_id] || {};
      return total + parseFloat(course.sell_price || 0);
    }, 0);
  };
  
  // Calculate total count for cart
  const getCartCount = () => {
    if (!stats) return 0;
    const cartStat = stats.find(item => item.status === "cart");
    return cartStat ? parseInt(cartStat.count) : 0;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' 
        ? 'asc' 
        : 'desc'
    }));
  };

  // Filter and sort orders based on search query and payment filter
  const getFilteredPaidOrders = () => {
    // Start with paid orders
    let filtered = [...paidOrders];
    
    // Apply payment method filter if not "all"
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(order => order.payment_method === paymentFilter);
    }
    
    // Apply search filter if there's a search query
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(order => {
        const student = studentDetails[order.student_id] || {};
        return student.name?.toLowerCase().includes(searchQuery.toLowerCase())
          || student.phone_number?.includes(searchQuery)
          || (order.order_id && order.order_id.toString().includes(searchQuery));
      });
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      if (sortConfig.key === 'created_at') {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (sortConfig.key === 'amount_paid') {
        const amountA = parseFloat(a.amount_paid || 0);
        const amountB = parseFloat(b.amount_paid || 0);
        return sortConfig.direction === 'asc' ? amountA - amountB : amountB - amountA;
      }
      
      return 0;
    });
  };

  // Filter and sort cart orders
  const getFilteredCartOrders = () => {
    let filtered = [...cartOrders];
    
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(order => {
        const student = studentDetails[order.student_id] || {};
        return student.name?.toLowerCase().includes(searchQuery.toLowerCase())
          || student.phone_number?.includes(searchQuery)
          || (order.order_id && order.order_id.toString().includes(searchQuery));
      });
    }
    
    return filtered.sort((a, b) => {
      if (sortConfig.key === 'created_at') {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      return 0;
    });
  };

  // Handle viewing student details
  const handleViewStudent = (studentId) => {
    window.open(`/view-student/${studentId}`, '_blank', 'noopener,noreferrer');
  };

  // Handle contacting via WhatsApp
  const handleContactWhatsApp = (phoneNumber) => {
    if (!phoneNumber || phoneNumber === 'N/A') {
      alert('No phone number available for this student');
      return;
    }
    
    // Ensure phone number starts with +91
    const formattedNumber = phoneNumber.startsWith('+91') 
      ? phoneNumber.replace(/\D/g, '')
      : `91${phoneNumber.replace(/\D/g, '')}`;
    
    // Open WhatsApp web with the formatted number
    window.open(`https://wa.me/${formattedNumber}`, '_blank', 'noopener,noreferrer');
  };

  return ( <div className="bg-white rounded-2xl shadow-md p-6">
    {/* Header Section */}
    <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Orders Dashboard</h2>
        <div className="flex items-center space-x-4">
          <CreateOrderButton onOrderCreated={handleOrderCreated} />
          <button 
            onClick={fetchAllData}
            className="text-gray-600 hover:text-blue-600 transition-colors"
            title="Refresh"
          >
            <FiRefreshCw size={20} />
          </button>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex mb-6 border-b">
        <button
          className={`px-4 py-2 mr-4 font-medium text-sm ${
            activeTab === 'paid'
              ? 'border-b-2 border-blue-500 text-blue-600 -mb-px'
              : 'text-gray-600 hover:text-blue-500'
          }`}
          onClick={() => setActiveTab('paid')}
        >
          Paid Orders
          <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
            {getTotalCompletedCount()}
          </span>
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'cart'
              ? 'border-b-2 border-blue-500 text-blue-600 -mb-px'
              : 'text-gray-600 hover:text-blue-500'
          }`}
          onClick={() => setActiveTab('cart')}
        >
          Cart
          <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
            {getCartCount()}
          </span>
        </button>
      </div>

      {/* Stats Section */}
      {activeTab === 'paid' && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Total Paid</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(getTotalCompleted())}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Cash Payments</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(getTotalByPaymentMethod('cash'))}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">UPI Payments</p>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(getTotalByPaymentMethod('upi'))}</p>
          </div>
          <div className="bg-blue-100 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Online Payments</p>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(getTotalByPaymentMethod('razorpay_online'))}</p>
          </div>
        </div>
      )}

      {activeTab === 'cart' && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-yellow-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Items in Cart</p>
            <p className="text-2xl font-bold text-yellow-600">{getCartCount()}</p>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Potential Revenue</p>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(getCartTotal())}</p>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          {activeTab === 'paid' && (
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="all">All Payment Methods</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="razorpay_online">Razorpay Online</option>
            </select>
          )}
          <div className="relative">
            <input 
              type="text"
              placeholder="Search by name, phone, or order ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border border-gray-300 px-3 py-2 rounded-lg pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <IoSearchOutline className="h-5 w-5 absolute left-2 top-3 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center">
                    Date
                    {sortConfig.key === 'created_at' && (
                      <span className="ml-2">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course
                </th>
                {activeTab === 'paid' && (
                  <>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('amount_paid')}
                    >
                      <div className="flex items-center">
                        Amount
                        {sortConfig.key === 'amount_paid' && (
                          <span className="ml-2">
                            {sortConfig.direction === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Method
                    </th>
                  </>
                )}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activeTab === 'paid' ? (
                getFilteredPaidOrders().length > 0 ? (
                  getFilteredPaidOrders().map((order, index) => {
                    const student = studentDetails[order.student_id] || {};
                    const course = courseDetails[order.content_id] || {};
                    
                    return (
                      <tr key={order.order_id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          #{order.order_id}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              {student.name ? student.name.charAt(0).toUpperCase() : '?'}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{student.name || 'Unknown Student'}</div>
                              <div className="text-sm text-gray-500">{student.phone_number || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{course.title || 'Unknown Course'}</div>
                          <div className="text-sm text-gray-500">
                            List Price: {formatCurrency(parseFloat(course.sell_price || 0))}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(parseFloat(order.amount_paid || 0))}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              order.payment_method === 'cash' 
                                ? 'bg-green-100 text-green-800' 
                                : order.payment_method === 'upi'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {order.payment_method === 'cash' 
                              ? 'Cash' 
                              : order.payment_method === 'upi'
                              ? 'UPI'
                              : 'Razorpay'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={() => handleViewStudent(order.student_id)}
                              className="text-blue-500 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50"
                              title="View Student"
                            >
                              <FiEye size={18} />
                            </button>
                            <button
  onClick={() => handleContactWhatsApp(student.phone_number)}
  className="text-green-500 hover:text-green-700 p-2 rounded-full hover:bg-green-50"
  title="Contact on WhatsApp"
>
  <FaWhatsapp size={18} />
</button>

                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="px-4 py-4 text-center text-sm text-gray-500">
                      No paid orders found.
                    </td>
                  </tr>
                )
              ) : getFilteredCartOrders().length > 0 ? (
                getFilteredCartOrders().map((order, index) => {
                  const student = studentDetails[order.student_id] || {};
                  const course = courseDetails[order.content_id] || {};
                  
                  return (
                    <tr key={order.order_id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        #{order.order_id}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            {student.name ? student.name.charAt(0).toUpperCase() : '?'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{student.name || 'Unknown Student'}</div>
                            <div className="text-sm text-gray-500">{student.phone_number || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{course.title || 'Unknown Course'}</div>
                        <div className="text-sm text-gray-500">
                          List Price: {formatCurrency(parseFloat(course.sell_price || 0))}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handleViewStudent(order.student_id)}
                            className="text-blue-500 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50"
                            title="View Student"
                          >
                            <FiEye size={18} />
                          </button>
                          <button
                            onClick={() => handleContactWhatsApp(student.phone_number)}
                            className="text-green-500 hover:text-green-700 p-2 rounded-full hover:bg-green-50"
                            title="Contact on WhatsApp"
                          >
                            <FiMessageCircle size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-4 text-center text-sm text-gray-500">
                    No items in cart.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* No Data */}
          {(activeTab === 'paid' && getFilteredPaidOrders().length === 0 && !loading) ||
           (activeTab === 'cart' && getFilteredCartOrders().length === 0 && !loading) ? (
            <div className="text-center py-10 text-gray-500">
              {activeTab === 'paid' ? 'No paid orders found.' : 'No items in cart.'}
            </div>
          ) : null}
        </div>
      )}
      
      {/* Pagination */}
      {((activeTab === 'paid' && getFilteredPaidOrders().length > 0) || 
        (activeTab === 'cart' && getFilteredCartOrders().length > 0)) && (
        <div className="flex items-center justify-between mt-4">
          <div>
            <p className="text-sm text-gray-700">
              Showing 1 to {activeTab === 'paid' 
                ? getFilteredPaidOrders().length 
                : getFilteredCartOrders().length} of {activeTab === 'paid' 
                ? getFilteredPaidOrders().length 
                : getFilteredCartOrders().length} results
            </p>
          </div>
          <div className="flex space-x-1">
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &lt;
            </button>
            <button className="px-3 py-1 rounded border border-blue-500 bg-blue-50 text-blue-600">
              1
            </button>
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &gt;
            </button>
          </div>
        </div>
      )}
   
   </div>
  );
};
export default OrdersDashboard;